import { type Static, Type } from "@sinclair/typebox"
import { IsoTimestampSchema } from "../schemas"

export const MODERATION_INCIDENT_STORAGE_KEY =
  "craft-force.moderation-incidents"
export const MODERATION_INCIDENT_STORAGE_VERSION = 1

export const ModerationIncidentStatusSchema = Type.Union([
  Type.Literal("pending"),
  Type.Literal("confirmed"),
  Type.Literal("dismissed"),
])

export type ModerationIncidentStatus = Static<
  typeof ModerationIncidentStatusSchema
>

export const ModerationIncidentTypeSchema = Type.Union([
  Type.Literal("gamer_tag"),
  Type.Literal("display_name"),
  Type.Literal("card_name"),
  Type.Literal("hero_name"),
])

export type ModerationIncidentType = Static<typeof ModerationIncidentTypeSchema>

export const ModerationIncidentSchema = Type.Object({
  id: Type.String({ minLength: 1 }),
  type: ModerationIncidentTypeSchema,
  value: Type.String(),
  actorEmail: Type.Optional(Type.String()),
  createdAt: IsoTimestampSchema,
  updatedAt: IsoTimestampSchema,
  status: ModerationIncidentStatusSchema,
})

export type ModerationIncident = Static<typeof ModerationIncidentSchema>

export const ModerationIncidentStoreSchema = Type.Array(
  ModerationIncidentSchema,
)
export type ModerationIncidentStore = Static<
  typeof ModerationIncidentStoreSchema
>

export const EMPTY_MODERATION_INCIDENT_STORE: ModerationIncidentStore = []

export function createModerationIncident(
  type: ModerationIncidentType,
  value: string,
  actorEmail?: string,
): ModerationIncident {
  const now = new Date().toISOString()
  return {
    id: `mod_${Date.now().toString(36)}_${Math.random().toString(36).slice(2, 8)}`,
    type,
    value: value.trim(),
    actorEmail,
    createdAt: now,
    updatedAt: now,
    status: "pending",
  }
}

export function deriveAccountStanding(
  incidents: readonly ModerationIncident[],
  actorEmail: string,
): {
  level: "good" | "warned" | "restricted"
  activeCount: number
  latestIncidentAt: string | null
} {
  const relevant = incidents.filter(
    (incident) =>
      incident.actorEmail === actorEmail && incident.status !== "dismissed",
  )
  const latestIncidentAt = relevant[0]?.createdAt ?? null
  if (relevant.length >= 3) {
    return {
      level: "restricted",
      activeCount: relevant.length,
      latestIncidentAt,
    }
  }
  if (relevant.length >= 1) {
    return { level: "warned", activeCount: relevant.length, latestIncidentAt }
  }
  return { level: "good", activeCount: 0, latestIncidentAt: null }
}

export function updateModerationIncidentStatus(
  incidents: readonly ModerationIncident[],
  incidentId: string,
  status: ModerationIncidentStatus,
): ModerationIncidentStore {
  const updatedAt = new Date().toISOString()
  return incidents.map((incident) =>
    incident.id === incidentId ? { ...incident, status, updatedAt } : incident,
  )
}
